<template>
    <div class="inner-page-section" v-if="isDataLoaded">

        <div class="card" v-if="hasActiveConfiguration">
            <div class="card-header">
                <i class="fa fa-history card-header--icon"></i>
                <span class="card-header--title">Whitelabel Configuration History</span>
            </div>
            <div class="card-content">
                <p class="info-text">
                    Affiliate <b>{{ affiliate.code }}</b> has an active whitelabel configuration. Edit the configuration
                    in the
                    form bellow, and click "Save Configuration" to publish a new version of it.
                </p>
                <table class="card-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>UID</th>
                            <th>Affiliation Code</th>
                            <th>Schema Version</th>
                            <th>Created At</th>
                            <th>Active</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="conf in configurationsHistory.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))"
                            :key="conf.id">
                            <td>{{ conf.id }}</td>
                            <td>{{ conf.configurationUid }}</td>
                            <td>{{ conf.affiliateCode }}</td>
                            <td>{{ conf.schemaVersion }}</td>
                            <td>{{ dateString(conf.createdAt) }}</td>
                            <td>
                                <span v-if="conf.isActive"><b>Active</b></span>
                                <span v-else style="color:gray;">Inactive</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div class="card" v-else>
            <div class="card-header">
                <i class="fa fa-cog card-header--icon"></i>
                <span class="card-header--title">Whitelabel Configuration</span>
            </div>
            <div class="card-content">
                <p>
                    No active configuration exists for <b>{{ affiliate.code }}</b>. Add a configuration in the form
                    bellow, and click "Save Configuration" to activate it.
                </p>
            </div>
        </div>


        <div class="two-column-grid">
            <div class="card">
                <div class="card-header">
                    <i class="fa fa-code card-header--icon"></i>
                    <span class="card-header--title">Configuration JSON</span>
                </div>
                <div class="card-content">
                    <div class="form-group">
                        <div class="btn-group">
                            <button :class="{ 'active': editMode === 'FORM' }" @click="editMode = 'FORM'">Edit with
                                form</button>
                            <button :class="{ 'active': editMode === 'RAW' }" @click="editMode = 'RAW'">Edit as
                                JSON</button>
                        </div>
                    </div>
                </div>
                <div class="card-content" v-if="editMode === 'RAW'">
                    <textarea v-model="rawConfiguration" style="width:100%; min-height: 1600px;"></textarea>
                </div>

                <div class="card-content" v-if="editMode === 'FORM'">

                    <WhitelabelConfigForm :config="config" @onUpdate="updateConfig"
                        :affiliationCodes="affiliationCodes" />

                    <!-- Head Section -->
                    <div class="form-group">
                        <label>Head Entries</label>
                        <div v-for="(head, idx) in config.head" :key="'head-' + idx" class="input-group"
                            style="flex-direction:column;align-items:flex-start;gap:0.5rem; border:1px solid #eee; padding:0.5rem; margin-bottom:0.5rem;">
                            <label>Title <input type="text" v-model="head.title" placeholder="Title" /></label>
                            <label>Locale <input type="text" v-model="head.locale"
                                    placeholder="Locale (e.g. en)" /></label>
                            <label>Description <input type="text" v-model="head.description"
                                    placeholder="Description" /></label>
                            <div style="width:100%">
                                <label>Meta OG Image <input type="text" v-model="head.meta.og.image"
                                        placeholder="OG Image URL" /></label>
                                <label>Meta OG Title <input type="text" v-model="head.meta.og.title"
                                        placeholder="OG Title" /></label>
                                <label>Meta OG Description <input type="text" v-model="head.meta.og.description"
                                        placeholder="OG Description" /></label>
                                <label>Meta Robots
                                    <select v-model="head.meta.robots">
                                        <option value="index,follow">index,follow</option>
                                        <option value="noindex,follow">noindex,follow</option>
                                        <option value="index,nofollow">index,nofollow</option>
                                        <option value="noindex,nofollow">noindex,nofollow</option>
                                    </select>
                                </label>
                            </div>
                            <button type="button" class="btn-sm danger-btn"
                                @click="config.head.splice(idx, 1)">Remove</button>
                        </div>
                        <button type="button"
                            @click="config.head.push({ meta: { og: { image: '', title: '', description: '' }, robots: '' }, title: '', locale: '', description: '' })">Add
                            Head Entry</button>
                    </div>


                    <!-- Header Section -->
                    <div class="form-group">
                        <label>Header Background Color</label>
                        <input type="text" v-model="config.header.backgroundColor" placeholder="#ffffff" />
                    </div>
                    <div class="form-group">
                        <label>Header Return URLs</label>
                        <div v-for="(url, idx) in config.header.returnUrls" :key="'header-return-' + idx"
                            class="input-group"
                            style="flex-direction:column;align-items:flex-start;gap:0.5rem; border:1px solid #eee; padding:0.5rem; margin-bottom:0.5rem;">
                            <label>URL <input type="text" v-model="url.url" placeholder="https://..." /></label>
                            <label>Label <input type="text" v-model="url.label" placeholder="Label" /></label>
                            <label>Locale <input type="text" v-model="url.locale"
                                    placeholder="Locale (e.g. en)" /></label>
                            <button type="button" class="btn-sm danger-btn"
                                @click="config.header.returnUrls.splice(idx, 1)">Remove</button>
                        </div>
                        <button type="button" class="btn-sm primary-btn"
                            @click="config.header.returnUrls.push({ url: '', label: '', locale: '' })">Add
                            Return URL</button>
                    </div>
                    <div class="form-group">
                        <label>Header Height</label>
                        <input type="text" v-model="config.header.height" placeholder="60px" />
                    </div>


                    <!-- Footer Section -->
                    <div class="form-group">
                        <label>Footer Background Color</label>
                        <input type="text" v-model="config.footer.backgroundColor" placeholder="#ffffff" />
                    </div>
                    <div class="form-group">
                        <label>Footer Links</label>
                        <div v-for="(link, idx) in config.footer.links" :key="'footer-link-' + idx" class="input-group"
                            style="flex-direction:column;align-items:flex-start;gap:0.5rem; border:1px solid #eee; padding:0.5rem; margin-bottom:0.5rem;">
                            <label>URL <input type="text" v-model="link.url" placeholder="https://..." /></label>
                            <label>Label <input type="text" v-model="link.label" placeholder="Label" /></label>
                            <label>Locale <input type="text" v-model="link.locale"
                                    placeholder="Locale (e.g. en)" /></label>
                            <button type="button" class="btn-sm danger-btn"
                                @click="config.footer.links.splice(idx, 1)">Remove</button>
                        </div>
                        <button type="button" class="btn-sm primary-btn"
                            @click="config.footer.links.push({ url: '', label: '', locale: '' })">
                            Add Footer Link
                        </button>
                    </div>

                </div>

                <div class="flex-row flex-row--stretch mb10">
                    <div class="f1 mr10" :class="{ 'json-preview-errors': isConfigurationInvalid }"><span
                            v-if="isConfigurationInvalid">{{ validationError }}</span></div>
                    <button class="save-btn" @click="onSaveClicked" :disabled="isWaiting">
                        Save Configuration <span v-if="isWaiting"><i class="fa fa-spinner fa-spin"></i></span>
                    </button>
                </div>
            </div>
            <div class="card">
                <div class="card-header">
                    <i class="fa fa-code card-header--icon"></i>
                    <span class="card-header--title">Configuration Preview</span>
                </div>
                <div class="card-content">
                    <div class="flex-row flex-row--stretch mb10">
                        <div class="json-preview-errors mb5 f1" v-if="isConfigurationInvalid">{{ validationError }}
                        </div>
                        <button class="save-btn" @click="onSaveClicked" :disabled="isWaiting" v-else>
                            Save Configuration <span v-if="isWaiting"><i class="fa fa-spinner fa-spin"></i></span>
                        </button>
                    </div>

                    <pre class="json-preview"
                        :class="{ 'json-preview--invalid': isConfigurationInvalid }">{{ config }}</pre>
                </div>
            </div>

        </div>


    </div>
</template>


<script>
import ApiGateway from '../services/ApiGateway'
import dayjs from 'dayjs';
import WhitelabelConfigForm from '../components/WhitelabelConfigForm.vue';
import { createAffiliate } from '../models/createAffiliate';
import { whitelabelSchema } from '../models/whitelabel.schema';
import { getDefaultWhitelabelConfiguration } from '../models/getDefaultWhitelabelConfiguration';

const isSameConfiguration = (config1, config2) => {
    return JSON.stringify(config1) === JSON.stringify(config2);
};

export default {
    name: 'CreateWhitelabel',
    components: {
        WhitelabelConfigForm
    },
    props: {
        code: {
            type: String,
            required: false,
        },
    },
    data: () => ({
        editMode: 'FORM', // 'FORM' | 'RAW'
        isWaiting: true,
        affiliate: createAffiliate(),
        configurationsHistory: [],
        rawConfiguration: '',
        isConfigurationInvalid: false,
        config: null,
        latestConfiguration: null,
        validationError: '',
    }),
    watch: {
        config: {
            handler: async function (newVal) {
                // Update rawConfiguration when the config has changed through the form
                if (!isSameConfiguration(newVal, JSON.parse(this.rawConfiguration))) {
                    this.rawConfiguration = JSON.stringify(this.config, null, 2);
                }
            },
            deep: true
        },
        async rawConfiguration(newVal) {
            try {
                if (!newVal) {
                    this.isConfigurationInvalid = false;
                    return;
                }

                // Avoid unnecessary re-parsing
                const currentConfigurationValue = JSON.stringify(this.config);
                if (newVal === currentConfigurationValue) return;

                // update the config object when rawconfiguration has changed through the textarea
                this.config = JSON.parse(newVal);
                this.isConfigurationInvalid = false;

                const schema = whitelabelSchema(this.affiliationCodes);

                try {
                    await schema.validate(this.config, { abortEarly: false, strict: true });
                } catch (validationError) {
                    // Log all validation errors in the console for debugging
                    console.log('Validation errors:', validationError.errors);
                }


                // Validate configuration against schema and throw the first error found
                schema.validate(this.config)
                    .then(() => {
                        console.log('✅ Config is valid');
                        this.validationError = '';
                    })
                    .catch(err => {
                        this.validationError = err.errors.join(', ');
                        this.isConfigurationInvalid = true;
                    });

            } catch (e) {
                console.log('Invalid JSON', e.message);
                this.isConfigurationInvalid = true;
                this.validationError = 'Invalid JSON';
            }
        }
    },
    created() {
        this.isWaiting = true;
        ApiGateway.getAffiliateByCode(this.code).then(r => this.loadUserConfiguration(r));
    },
    methods: {
        updateConfig(newConfig) {
            this.config = newConfig;
        },
        dateString(date) {
            return dayjs(date).format('HH:mm, DD/MM/YYYY');
        },
        loadUserConfiguration(response) {
            this.affiliate = createAffiliate(response.data.user);
            this.isWaiting = false;
            this.configurationsHistory = response.data.user.whitelabelConfigurations || [];
            const activeConfig = this.configurationsHistory.find(c => c.isActive);
            if (activeConfig) {
                this.config = activeConfig.whitelabelSettings;
                this.rawConfiguration = JSON.stringify(this.config, null, 2);
                this.latestConfiguration = this.rawConfiguration;
            } else {
                this.config = getDefaultWhitelabelConfiguration(this.affiliationCodes[0] || '');
                this.rawConfiguration = JSON.stringify(this.config, null, 2);
            }
        },
        async onSaveClicked() {

            if (this.latestConfiguration === this.rawConfiguration) {
                this.$store.dispatch('notificationsModule/warningNotify', 'No changes to save.');
                return;
            }

            try {

                const schema = whitelabelSchema(this.affiliationCodes);
                await schema.validate(this.config);

                console.log('✅ Config is valid');
                this.isConfigurationInvalid = false;
                this.validationError = '';
            } catch (e) {
                this.isConfigurationInvalid = true;
                this.validationError = 'Schema error: ' + e.errors.join(', ');
                console.error('❌ Schema validation failed:', e.errors);
                return;
            }

            try {
                this.isWaiting = true;
                const response = await ApiGateway.saveWhitelabelConfiguration(this.affiliate, this.config);
                this.loadUserConfiguration(response);
                this.$store.dispatch('notificationsModule/successNotify', 'Configuration saved!');
            } catch (e) {
                console.log(e)
                this.$store.dispatch('notificationsModule/errorNotify', e.message);
                this.isWaiting = false;
                return;
            }

        }
    },
    computed: {
        isDataLoaded() {
            return !!this.config;
        },
        hasActiveConfiguration() {
            return this.configurationsHistory.some(c => c.isActive);
        },
        affiliationCodes() {
            if (!this.affiliate.masterCode) {
                return [];
            }

            return (this.affiliate.masterCode || '').replace(/ /g, ',').replace(/,,/g, ',').split(',');
        }
    }
}
</script>

<style scoped>
/* Custom styling for JSON preview <pre> */
.json-preview {
    background: #f7f7f7;
    padding: 1rem;
    border-radius: 6px;
    font-size: 0.97em;
    font-family: 'Fira Mono', 'Consolas', 'Menlo', monospace;
    color: #222;
    line-height: 1.5;
    overflow-x: auto;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
    border: 1px solid #e0e0e0;
    white-space: break-spaces;
}

.json-preview--invalid {
    border: 1px solid red;
}

.json-preview-errors {
    padding: 1rem;
    border-radius: 6px;
    border: 1px solid red;
    background-color: #ffe9e9;
}


.input-group {
    display: flex;
    gap: 0.3rem;
    margin-bottom: 0.3rem;
    justify-content: space-between;
}

.input-group input {
    flex: 1;
}
</style>
